/**
 * Where the user last put a list's members, ahead of the search index confirming it.
 *
 * The board and the table are two views of the same list, and a stage set in one has to show
 * in the other at once, so this overlay lives outside them both: module state, one map per
 * list, read and written by whoever needs it. `stage: null` is a tombstone left by a removal:
 * the member belongs in NO stage, not just a different one.
 *
 * Nothing here expires anything. The reader owns the TTL, so a single read cycle's parallel
 * lane reads can all agree on one clock.
 */
export type ListPlacement<T> = { stage: string | null; item: T; at: number }

// Each list's items are one type to its own caller, but this store holds every list's, so the
// item type only exists at the call boundary.
type StoredPlacements = Map<string, ListPlacement<unknown>>

/**
 * How long a placement (an add, a move, or a removal's tombstone) keeps overriding what a read
 * says, before it is let go regardless. The overlay only exists to cover the second or two the
 * search index lags a write; ten seconds is generous and self-healing, so a card that sorts past
 * page 0, a filter that hides it, or a stage that got deleted never pins it forever.
 */
export const PLACED_TTL_MS = 10_000

/**
 * How long after a list write a read can trust the search index. A read inside the lag gets
 * wrong totals and "load more" even where a placement keeps the cards drawn. Shorter than
 * `PLACED_TTL_MS`, so the placements still cover that read.
 */
export const INDEX_SETTLE_MS = 3_000

const byList = new Map<string, StoredPlacements>()

/** The live map for a list, created on first use: callers mutate it directly. */
export function placementsFor<T>(listId: string): Map<string, ListPlacement<T>> {
  const existing = byList.get(listId)
  if (existing) return existing as unknown as Map<string, ListPlacement<T>>
  const created = new Map<string, ListPlacement<T>>()
  byList.set(listId, created as unknown as StoredPlacements)
  return created
}

export function recordPlacement<T>(listId: string, itemId: string, placement: ListPlacement<T>): void {
  placementsFor<T>(listId).set(itemId, placement)
}

/** A read's items without the ones a removal's tombstone, still inside the index's lag, keeps off the list. */
export function withoutRemovedMembers<T extends { id?: string | null }>(
  listId: string,
  items: readonly T[],
  now: number = Date.now(),
): { kept: T[]; dropped: number } {
  const placed = placementsFor<T>(listId)
  const kept = items.filter((item) => {
    const placement = item.id ? placed.get(item.id) : undefined
    return !(placement?.stage === null && now - placement.at <= PLACED_TTL_MS)
  })
  return { kept, dropped: items.length - kept.length }
}

/** Every list with a placement for this item, so a reader can find a write the index has not returned at all. */
export function listIdsPlacedFor(itemId: string): string[] {
  return [...byList].filter(([, placements]) => placements.has(itemId)).map(([listId]) => listId)
}

/** One list, or every list when no list is named. */
export function clearPlacements(listId?: string): void {
  if (listId === undefined) {
    // Emptied in place as well as dropped, so a map a caller is still holding goes empty too.
    for (const placements of byList.values()) placements.clear()
    byList.clear()
    return
  }
  byList.get(listId)?.clear()
  byList.delete(listId)
}
